/**
 * ESS raise/request visibility: Quick Panel RBAC ∧ attendance policy (where applicable).
 * Approvals stay on separate Quick Panel approval screens.
 */

import { isRequestTypeAllowedByPolicy } from "./attendanceRequestAccess";
import { isCompanyModuleEnabled } from "../utils/companyModules";

/** Request types that also need a matching attendance process policy flag. */
export const ATTENDANCE_POLICY_REQUEST_KEYS = new Set([
  "change_shift",
  "change_wo",
  "regularization_checks",
  "short_leave",
  "leave_request",
  "od",
  "comp_off",
  "ot",
]);

/** Self-raise / My requests types (not approval-only). */
export const ESS_RAISE_REQUEST_KEYS = [
  "leave_request",
  "change_shift",
  "change_wo",
  "regularization_checks",
  "short_leave",
  "od",
  "ot",
  "restricted_holiday",
  "resignation",
  "tax_declaration",
  "reimbursement_opt_in",
  "reimbursement_claim",
  "ticket_request",
  "mrf_request",
];

export function allowEssQuickPanelScreen(hasScreenAccess, isAdmin, screenKey) {
  if (screenKey === "tax_declaration" && !isCompanyModuleEnabled("payroll")) return false;
  if (
    (screenKey === "reimbursement_opt_in" ||
      screenKey === "reimbursement_claim" ||
      screenKey === "reimbursement") &&
    !isCompanyModuleEnabled("payroll")
  ) {
    return false;
  }
  if (isAdmin) return true;
  if (typeof hasScreenAccess !== "function" || !screenKey) return false;
  if (hasScreenAccess("ess_quick_panel", screenKey, "view")) return true;
  // Legacy single key still grants both Opt-in and Claim until roles are re-saved.
  if (
    (screenKey === "reimbursement_opt_in" || screenKey === "reimbursement_claim") &&
    hasScreenAccess("ess_quick_panel", "reimbursement", "view")
  ) {
    return true;
  }
  return false;
}

/**
 * @param {{
 *   key: string,
 *   hasScreenAccess: Function,
 *   isAdmin?: boolean,
 *   policyAccess?: object|null,
 * }} opts
 */
export function canRaiseEssRequestType({
  key,
  hasScreenAccess,
  isAdmin = false,
  policyAccess = null,
}) {
  if (!key) return false;
  const rbacOk = allowEssQuickPanelScreen(hasScreenAccess, isAdmin, key);
  if (!rbacOk) return false;
  if (ATTENDANCE_POLICY_REQUEST_KEYS.has(key)) {
    return isRequestTypeAllowedByPolicy(key, policyAccess);
  }
  return true;
}

export function hasAnyEssRaiseRequestAccess({
  hasScreenAccess,
  isAdmin = false,
  policyAccess = null,
  keys = ESS_RAISE_REQUEST_KEYS,
}) {
  return (keys || []).some((key) =>
    canRaiseEssRequestType({ key, hasScreenAccess, isAdmin, policyAccess })
  );
}

/** Map Attendance Logs ⋮ actions → Quick Panel screen keys. */
export const DAY_ACTION_QUICK_PANEL_KEY = {
  leave: "leave_request",
  changeShift: "change_shift",
  changeWo: "change_wo",
  regularise: "regularization_checks",
  shortLeave: "short_leave",
  od: "od",
  compOff: "comp_off",
  ot: "ot",
  restrictedHoliday: "restricted_holiday",
};
